import React, { useState, useRef, useLayoutEffect } from "react";
import styles from "../../_css/UsersTable.module.css";
import {
  MoreVertical,
  User,
  Trash2,
} from "lucide-react";
import { CustomButtonOutline } from "../UI/CustomButtons";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { formatDate } from "../helper/formatters";

// const PAGE_SIZE = 5;
// const TOTAL_ENTRIES = 20;
const DROPDOWN_WIDTH = 180; // px

// Custom styles copied from SelectComponent
const dropdownMenuStyles = {
  position: "absolute",
  top: "36px",
  right: "0",
  minWidth: "180px",
  width: "180px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  zIndex: 10,
  padding: "6px 0",
  border: "1px solid #eaf6ff",
};
const dropdownOptionStyles = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 18px",
  fontSize: "13px",
  color: "#174291",
  background: "#F2F9FD",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  margin: "4px 8px",
  transition: "background 0.2s",
};
const dropdownOptionHover = {
  background: "#EFFCFB",
};

const groupedOptions = [
  {
    label: "By Status",
    options: [
      { value: "Active", label: "Active" },
      { value: "Expired", label: "Expired" },
    ],
  },
  {
    label: "By Subscription",
    options: [
      { value: "Free User", label: "Free User" },
      { value: "Premium User", label: "Premium User" },
    ],
  },
  {
    label: "By Language",
    options: [
      { value: "Arabic User", label: "Arabic User" },
      { value: "English User", label: "English User" },
    ],
  },
];

const groupedOptions2 = [
  {
    label: "By Name",
    options: [
      { value: "Name: A to Z", label: "Name: A to Z" },
      { value: "Name: Z to A", label: "Name: Z to A" },
    ],
  },
  {
    label: "By Sessions",
    options: [
      { value: "High to Low", label: "High to Low" },
      { value: "Low to High", label: "Low to High" },
    ],
  },
];

function RowActionsDropdown({ onViewProfile, onRemoveUser }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 180,
  });

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - DROPDOWN_WIDTH,
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className={styles.rowActionsBtn}
        onClick={() => setOpen((o) => !o)}
        tabIndex={0}
        aria-label="Row actions"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        ref={btnRef}
      >
        <MoreVertical size={22} color="#798797" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              ...dropdownMenuStyles,
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              minWidth: "180px",
              width: "180px",
              zIndex: 9999,
            }}
          >
            <div
              style={
                hovered === 0
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(0)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onViewProfile && onViewProfile();
              }}
            >
              <User size={18} style={{ color: "#174291" }} /> View Profile
            </div>
            <div
              style={
                hovered === 1
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(1)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={() => {
                setOpen(false);
                onRemoveUser && onRemoveUser();
              }}
            >
              <Trash2 size={18} style={{ color: "#174291" }} /> Remove User
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const UsersTable = ({
  tableTitle,
  headers,
  rows,
  variant = "blue",
  showViewAll = false,
}) => {
  const navigate = useNavigate();

  const formatColumnData = (data, key) => {
    // TODO: Add more keys to the arrays
    const currencyKeys = [
      "openai_cost",
      "elevenlabs_cost",
      "aws_transcribe_cost",
      "total_spend",
      "total_cost",
      "avg_spend_per_user",
    ];
    const capitalizeKeys = ["language", "sessions", "top_session"];

    // TODO: Add logic for keys
    if (key === "date_joined") return formatDate(data);
    if (capitalizeKeys.includes(key)) return _.capitalize(data);
    // if (currencyKeys.includes(key)) return `$${data || 0}`;
    if (currencyKeys.includes(key)) return `$${Number(data) > 0 ? Number(data).toFixed(4) : 0}`;
    if (key === "avg_duration_minutes") return `${data} min`;

    return data;
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.filtersRow}>
        <h5 className={styles.tableTitle}>{tableTitle}</h5>

        {showViewAll && (
          <CustomButtonOutline
            text="View All"
            onClick={() => navigate("/user-management")}
            padding="4px 12px"
          />
        )}
      </div>
      <hr />
      <div className={styles.tableWrapper}>
        <div className={styles.tableBorderWrapper}>
          <table className={styles.usersTable}>
            <thead>
              <tr
                style={{
                  background: variant === "blue" ? "#F3FAFF" : "#F0FDF4",
                }}
              >
                {headers.map((header, idx) => (
                  <th
                    style={{
                      width: ["Action"].includes(header.label)
                        ? "12px"
                        : "auto",
                      color: variant === "blue" ? "#174291" : "#14532B",
                    }}
                    key={idx}
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                return (
                  <tr key={idx}>
                    {headers.map((header, idxIn) => (
                      <td key={idxIn} className={styles.tableCell}>
                        {formatColumnData(row[header.key], header.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersTable;
